/**
 * @fileoverview Pure domain logic for AI Competitor Analysis. Everything here is
 * side-effect free and unit tested; components and hooks import from it rather
 * than reimplementing the rules.
 */

/** Run statuses in which the pipeline is still working. */
export const IN_FLIGHT_STATUSES = ["RECOMMENDING", "ANALYZING"];

/** Run statuses after which nothing further will change. */
export const TERMINAL_STATUSES = ["COMPLETED", "FAILED", "TIMED_OUT"];

/** Sections the results view renders, in display order. */
export const RESULT_SECTIONS = [
  "OVERALL_POSITION",
  "ESG_BENCHMARK",
  "PEER_LANDSCAPE",
];

/**
 * Whether a run has stopped changing and polling should end.
 *
 * @param {object|null} run - Run view from the API.
 * @returns {boolean} True once the run reached a terminal status.
 */
export const isTerminal = (run) =>
  Boolean(run) && TERMINAL_STATUSES.includes(run.status);

/**
 * Whether the pipeline is still working on a run.
 *
 * @param {object|null} run - Run view from the API.
 * @returns {boolean} True while the run is recommending or analysing.
 */
export const isInFlight = (run) =>
  Boolean(run) && IN_FLIGHT_STATUSES.includes(run.status);

/**
 * Finds one section on a run.
 *
 * @param {object|null} run - Run view from the API.
 * @param {string} section - Section name to look for.
 * @returns {object|null} The section, or null when the run has not created it yet.
 */
export const findSection = (run, section) =>
  run?.sections?.find((entry) => entry.section === section) || null;

/**
 * Reads a section's payload only once it has completed.
 *
 * @param {object|null} run - Run view from the API.
 * @param {string} section - Section name to read.
 * @returns {object|null} The payload, or null while it is pending or failed.
 *
 * @remarks
 * A failed section still carries whatever it last held, so completion is checked
 * rather than payload presence — otherwise a stale partial result could render
 * as if it were current.
 */
export const sectionPayload = (run, section) => {
  const entry = findSection(run, section);
  return entry && entry.status === "COMPLETED" ? entry.payload : null;
};

/**
 * Toggles one peer in a selection.
 *
 * @param {number[]} selectedIds - Currently selected peer ids.
 * @param {number} id - Peer to toggle.
 * @returns {number[]} The next selection.
 */
export const togglePeer = (selectedIds, id) =>
  selectedIds.includes(id)
    ? selectedIds.filter((entry) => entry !== id)
    : [...selectedIds, id];

/**
 * Whether a peer selection may be submitted.
 *
 * @param {number[]} selectedIds - Currently selected peer ids.
 * @returns {boolean} True when at least one and at most ten peers are chosen.
 *
 * @remarks
 * The upper bound mirrors the API's own limit, so the button disables rather
 * than the request being rejected after the user commits to it.
 */
export const canRunAnalysis = (selectedIds) =>
  selectedIds.length >= 1 && selectedIds.length <= 10;

/** Labels shown in the agent status checklist, in order. */
export const AGENT_STEPS = [
  "Sector identified",
  "Peer group identified",
  "Latest data collected",
  "ESG KPIs identified",
  "Benchmark analysis completed",
  "ESG position computed",
];

/**
 * Derives how far the agent checklist has advanced.
 *
 * @param {object|null} run - Run view from the API.
 * @returns {number} Count of completed steps, from zero to {@link AGENT_STEPS}.length.
 *
 * @remarks
 * The checklist is presentation over three real section statuses, so each
 * section completion advances it by two: the model does the naming and the
 * measuring in one call, and splitting them further would imply progress the
 * backend cannot actually report.
 */
export const agentStepsDone = (run) => {
  if (!run) return 0;
  if (run.status === "RECOMMENDING") return 1;

  let done = 2;
  if (sectionPayload(run, "PEER_LANDSCAPE")) done = 4;
  if (sectionPayload(run, "ESG_BENCHMARK")) done = 5;
  if (sectionPayload(run, "OVERALL_POSITION")) done = 6;
  return done;
};

/**
 * Narrows a KPI's series to the periods the user selected.
 *
 * @param {string[]} years - Every period the run covers, oldest first.
 * @param {string} period - One of latest, 2y or 3y.
 * @returns {{from: number, to: number}} Inclusive index bounds into the series.
 *
 * @remarks
 * Bounds rather than a sliced copy, so callers can apply the same window to
 * several parallel series without them drifting out of alignment.
 */
export const periodWindow = (years, period) => {
  const last = Math.max(0, years.length - 1);
  if (period === "3y") return { from: Math.max(0, last - 2), to: last };
  if (period === "2y") return { from: Math.max(0, last - 1), to: last };
  return { from: last, to: last };
};

/**
 * Picks the series a comparison is made against.
 *
 * @param {object} series - A KPI's you / peerAvg / leader arrays.
 * @param {string} compareMode - Either peer or leader.
 * @returns {number[]} The comparison series.
 */
export const comparisonSeries = (series, compareMode) =>
  compareMode === "leader" ? series.leader : series.peerAvg;

/**
 * Describes how the company compares on one KPI in the latest selected period.
 *
 * @param {object} kpi - A benchmark KPI.
 * @param {string} compareMode - Either peer or leader.
 * @param {number} index - Index of the period to describe.
 * @returns {string} Human-readable gap, direction accounted for.
 *
 * @remarks
 * `lowerIsBetter` flips the reading: the same numeric delta is a lead on
 * emissions intensity and a deficit on renewable share, so the sign alone
 * cannot be shown to the user.
 */
export const gapText = (kpi, compareMode, index) => {
  const mine = kpi.series.you[index];
  const theirs = comparisonSeries(kpi.series, compareMode)[index];
  const label = compareMode === "leader" ? "sector leader" : "peer average";

  if (!Number.isFinite(mine) || !Number.isFinite(theirs) || theirs === 0) {
    return `No comparison available against ${label}`;
  }

  const better = kpi.lowerIsBetter ? mine < theirs : mine > theirs;
  const pct = Math.abs(((mine - theirs) / theirs) * 100);
  return `${pct.toFixed(1)}% ${better ? "better than" : "behind"} ${label}`;
};
